import React, { useState, useEffect, useRef } from "react";
import { getCookie } from "../../utils/cookieManager";
import { COOKIE_NAMES } from "../../constants/constants";
import MessageDialog from "../../../components/dialogBox/messageDialog/messageDialog";
import { useNavigate } from "react-router-dom";
import { resetAllStoreStates } from "../../../store/store";
import useLogout from "../../hooks/useLogout";
import useSessionManager from "../../hooks/useSessionManager";

const TIMER_THRESHOLD = 2 * 60 * 1000; // 2 minutes in milliseconds

const SessionExpirationTimer = () => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const { getRemainingTime, isSessionExpiringSoon, checkSessionStatus } = useSessionManager({
    warningThreshold: 2, // Show warning 2 minutes before expiry
    activityExtension: 15, // Extend by 15 minutes on activity
    heartbeatInterval: 5, // Call verifyToken API every 5 minutes
  });

  const [showPopup, setShowPopup] = useState(false);
  const [expirationTime, setExpirationTime] = useState(1000);
  
  // Use refs to avoid dependency issues
  const getRemainingTimeRef = useRef(getRemainingTime);
  const isSessionExpiringSoonRef = useRef(isSessionExpiringSoon);
  const logoutRef = useRef(logout);
  
  // Update refs when functions change
  getRemainingTimeRef.current = getRemainingTime;
  isSessionExpiringSoonRef.current = isSessionExpiringSoon;
  logoutRef.current = logout;

  const handleCloseMessageModal = () => {
    setShowPopup(false);
    if (expirationTime <= 0) {
      resetAllStoreStates();
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    const checkTokenExpiration = async () => {
      const jwtToken = getCookie(COOKIE_NAMES.jwtToken);
      
      // If there's no JWT token, user should be logged out
      if (!jwtToken) {
        const currentPageUrl = window.location.pathname;
        if (
          !currentPageUrl.includes("/signin") &&
          !currentPageUrl.includes("/signup") &&
          !currentPageUrl.includes("/forgotPassword") &&
          !currentPageUrl.includes("/sendOtp") &&
          !currentPageUrl.includes("/verifyOtp") &&
          !currentPageUrl.includes("/auth/callback") &&
          currentPageUrl !== "/"
        ) {
          console.log("Calling logout due to missing JWT token");
          logout();
        }
        return;
      }
      
      // Get remaining time using session manager
      const timeRemaining = getRemainingTimeRef.current();
      setExpirationTime(timeRemaining);
      
      // Check if session is expiring soon
      if (isSessionExpiringSoonRef.current()) {
        const currentPageUrl = window.location.href;
        if (
          currentPageUrl.indexOf("/signin") <= 0 &&
          currentPageUrl.indexOf("/signup") <= 0
        ) {
          setShowPopup(true);
        }
      }
      
      // If session is expired, logout
      if (timeRemaining <= 0) {
        console.log("Session expired, logging out");
        logoutRef.current();
      }
    };
    
    checkTokenExpiration();

    // Check every 30 seconds for more responsive handling
    const intervalId = setInterval(checkTokenExpiration, 30 * 1000);

    return () => clearInterval(intervalId);
  }, []); // Remove dependencies to prevent infinite re-renders

  return (
    <div>
      {showPopup && (
        <MessageDialog
          message={"Your session is about to expire!"}
          open={showPopup}
          onClose={handleCloseMessageModal}
        />
      )}
    </div>
  );
};

export default SessionExpirationTimer;